import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { enablePush, isStandalone, pushPermission, pushSupported } from "@/lib/push";

const KEY = "ciftora_push_prompt_until";
const HIDE_DAYS = 14;
const TRIGGER_EVENTS = ["NEW_MESSAGE", "NEW_OFFER"];

/** Kullanıcı ilk mesaj/teklif bildirimini aldığında telefona bildirim izni ister. */
export const PushPrompt = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !pushSupported() || pushPermission() !== "default") return;
    if (Number(localStorage.getItem(KEY) || 0) > Date.now()) return;
    let alive = true;
    (async () => {
      try {
        const [{ data: status }, { data: inbox }] = await Promise.all([
          api.get("/push/status"),
          api.get("/notifications"),
        ]);
        if (!alive || !status.configured || status.enabled) return;
        const triggered = (inbox.items || []).some((n) => TRIGGER_EVENTS.includes(n.event));
        if (triggered) setVisible(true);
      } catch {
        /* sessiz */
      }
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(KEY, String(Date.now() + HIDE_DAYS * 86400000));
    setVisible(false);
  };

  const accept = async () => {
    setBusy(true);
    try {
      await enablePush();
      await api.post("/push/test").catch(() => {});
      toast.success("Bildirimler açıldı. Yeni mesaj ve tekliflerde haber vereceğiz.");
      setVisible(false);
    } catch (e) {
      toast.error(e.message || "Bildirim açılamadı");
      dismiss();
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;
  return (
    <div
      data-testid="push-prompt"
      style={{
        zIndex: 47,
        bottom: "calc(var(--mobile-bottom-nav-height) + env(safe-area-inset-bottom) + 12px)",
      }}
      className="fixed left-3 right-3 flex items-start gap-3 rounded-xl border border-border bg-card p-3 shadow-lg md:left-auto md:right-6 md:max-w-sm"
    >
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/20">
        <Bell className="h-5 w-5 text-primary" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-primary">Mesaj ve teklifleri kaçırma</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {isStandalone()
            ? "Yeni mesaj, teklif ve fiyat değişiminde telefonuna bildirim gönderelim mi?"
            : "Bildirim izni ver, yeni mesaj ve tekliflerde seni anında haberdar edelim."}
        </p>
        <div className="mt-2 flex gap-2">
          <button
            onClick={accept}
            disabled={busy}
            data-testid="push-prompt-accept"
            className="app-tap h-10 rounded-full bg-primary px-4 text-xs font-bold text-primary-foreground disabled:opacity-60"
          >
            {busy ? "Açılıyor…" : "Bildirimleri aç"}
          </button>
          <button
            onClick={dismiss}
            data-testid="push-prompt-later"
            className="app-tap h-10 rounded-full border border-border px-4 text-xs font-bold text-primary"
          >
            Daha sonra
          </button>
        </div>
      </div>
      <button onClick={dismiss} aria-label="Kapat" data-testid="push-prompt-dismiss" className="p-1">
        <X className="h-4 w-4 text-muted-foreground" />
      </button>
    </div>
  );
};
